// ============================================================
// 日主强弱（旺衰）判定 —— 通行简化打分法
// 依据：《定真论》「以日为己身」；通行教材三要素：
//   得令＝月令藏干与日主同气（提纲最重，权重加倍）
//   得地＝年/日/时支藏干中有日主同类之根（通根）
//   得势＝天干见比劫帮身、印星生身
// 计分：天干每字10分（日主不计）；地支藏干本气12·中气6·余气3；
//       月支全部得分×2（司令之权）。比值＝同党/(同党+异党)。
// 注意：此为通行简化法，真正用神尚需参合调候、通关、格局，
//       结果供入门理解参考。
// ============================================================

import { GAN_WX, SHENG_NEXT } from './tables.js';
import { tenGod as godOf } from './relations.js';

const SAME = new Set(['比肩', '劫财', '正印', '偏印']); // 同党：帮身生身

export function computeStrength({ pillars, dayGan }) {
  let same = 0, diff = 0;
  const detail = [];

  // 天干（日主自身不计分）
  for (const p of pillars) {
    if (p.key === 'day') continue;
    const g = godOf(dayGan, p.gan);
    const pt = 10;
    if (SAME.has(g)) same += pt; else diff += pt;
    detail.push({ where: `${p.label}干 ${p.gan}`, god: g, pts: pt, side: SAME.has(g) ? 'same' : 'diff' });
  }
  // 地支藏干
  for (const p of pillars) {
    const n = p.hide.length;
    p.hide.forEach((h, i) => {
      const g = godOf(dayGan, h);
      const base = i === 0 ? 12 : i === 1 ? 6 : 3;
      const pt = p.key === 'month' ? base * 2 : base; // 月令加权
      if (SAME.has(g)) same += pt; else diff += pt;
      detail.push({
        where: `${p.label}支${p.zhi}·藏${h}${i === 0 ? '(本气)' : i === 1 ? '(中气)' : '(余气)'}${p.key === 'month' ? '×2' : ''}`,
        god: g, pts: pt, side: SAME.has(g) ? 'same' : 'diff',
      });
    });
  }

  const ratio = same / Math.max(1, same + diff);
  let level, levelPlain;
  if (ratio >= 0.72) { level = '身强'; levelPlain = '你八字里「帮你的人和你自己的底气」明显占上风——像家里人多钱多、腰杆硬，做事有主见扛得住压力。'; }
  else if (ratio >= 0.58) { level = '偏强'; levelPlain = '你的底气略多于消耗，整体稳中有劲，适合主动做事、当领头人。'; }
  else if (ratio >= 0.42) { level = '中和'; levelPlain = '帮扶与消耗基本打平——能量收支平衡，是最省心的状态，顺环境而动即可。'; }
  else if (ratio >= 0.28) { level = '偏弱'; levelPlain = '消耗略大于补给——像一个人干几个人的活，需要多借力、少硬扛。'; }
  else { level = '身弱'; levelPlain = '八字里「消耗你的力量」明显占多数——先天能量储备少，更要懂得借势、找搭档、靠团队。'; }

  // ---- 三要素 ----
  const monthP = pillars.find(p => p.key === 'month');
  const mZhu = monthP.hide[0]; // 月令本气
  const dWx = GAN_WX[dayGan];
  let deLing;
  if (GAN_WX[mZhu] === dWx) deLing = { ok: 2, text: `月令本气即${dWx}，日主当令而旺` };
  else if (monthP.hide.includes(dayGan)) deLing = { ok: 1, text: `月令藏干中含${dayGan}，通气有余` };
  else if (SHENG_NEXT[GAN_WX[mZhu]] === dWx || GAN_WX[mZhu] === SHENG_NEXT[dWx]) {
    deLing = SHENG_NEXT[GAN_WX[mZhu]] === dWx
      ? { ok: 1, text: `月令${GAN_WX[mZhu]}生${dWx}，得月令相生` }
      : { ok: 0, text: `日主${dWx}生月令${GAN_WX[mZhu]}，泄气于提纲` };
  } else deLing = { ok: 0, text: `月令${mZhu}(${GAN_WX[mZhu]})不帮${dayGan}(${dWx})，谓之失令` };

  // 得地：年日时支通根
  const roots = [];
  for (const p of pillars) {
    if (p.key === 'year' || p.key === 'day' || p.key === 'time') {
      if (p.hide.some(h => GAN_WX[h] === dWx)) roots.push(p.label);
    }
  }
  const deDi = roots.length
    ? { ok: roots.length >= 2 ? 2 : 1, text: `${dayGan}在${roots.join('、')}藏干中有根（通根）` }
    : { ok: 0, text: `四柱地支无${dWx}之根，日主无根漂浮` };

  // 得势：天干印比
  const helpers = pillars.filter(p => p.key !== 'day' && SAME.has(godOf(dayGan, p.gan))).map(p => `${p.label}干${p.gan}`);
  const deShi = helpers.length
    ? { ok: helpers.length >= 2 ? 2 : 1, text: `天干有${helpers.join('、')}帮身生身` }
    : { ok: 0, text: '天干无比劫印星帮身' };

  const flags = (deLing.ok ? 1 : 0) + (deDi.ok ? 1 : 0) + (deShi.ok ? 1 : 0);

  // 喜用方向（通行原则：强则喜克泄耗，弱则喜生扶）
  let favor, favorPlain;
  if (level === '身强' || level === '偏强') {
    favor = ['官杀（磨炼管束）', '财星（目标实务）', '食伤（表达发挥）'];
    favorPlain = '你已经很有底气和资源，最怕「再多帮手」变成固执内耗；适合给自己找目标、找对手、多输出——把力气用出去反而顺。';
  } else if (level === '身弱' || level === '偏弱') {
    favor = ['印星（学习贵人）', '比劫（朋友搭档）'];
    favorPlain = '你的能量有限，不适合单打独斗硬扛压力；适合先充电学习、多靠贵人朋友协作，借力使力。';
  } else {
    favor = ['保持五行流通', '随大运流年调整'];
    favorPlain = '你的强弱接近平衡，没有明显短板；行运走到哪边，就顺势补哪边即可。';
  }

  return {
    level, ratio, samePts: same, diffPts: diff,
    levelPlain,
    deLing, deDi, deShi, flags,
    favor, favorPlain,
    detail,
    note: '此为通行简化打分法（天干10分、地支本气12/中气6/余气3、月令×2），供入门理解；真正用神需结合调候、通关、格局综合判断。',
  };
}
